import axios from 'axios'
import ls from 'local-storage'
import clsx from 'clsx'
import { useState, useEffect, useCallback, useRef } from 'react'
import Chart from 'react-apexcharts'
import PropTypes from 'prop-types'
import { basicColors } from 'src/theme'
import moment from 'moment'
import { copyTextToClipboard } from 'src/utils/general'
import { returnDuration } from '../pv/utils'
import ConfirmationDialogRaw from 'src/components/ConfirmationDialogRaw'
import {
  Checkbox,
  Button,
  Link,
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Paper,
  CircularProgress,
  Tooltip,
  SvgIcon,
  IconButton,
  Fab,
  Typography,
  Grid,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  makeStyles,
  TableHead,
  useMediaQuery,
  TextField,
  Chip
} from '@material-ui/core'

import {
  X as CloseIcon,
  XSquare as CloseIconSquare,
  Slash as ForceCloseIcon,
  Edit3 as EditIcon,
  LogOut as LogOutIcon,
  Copy as CopyIcon,
  Check as OkIcon,
  CheckSquare as OkIconSquare
} from 'react-feather'
import { alog } from 'src/utils/apioLog'
import useAuth from 'src/hooks/useAuth'
import { getDeviceLogs } from 'src/utils/general'
import { putEditAnomaly, putForceCloseAnomaly } from 'src/slices/cchp/plantView'
import { configuration } from 'src/config'
import { isUUIDOrUri } from './AnomaliesListView'

// stile CSS generale
const useStyles = makeStyles((theme) => ({
  sidenavContainer: {
    width: '45vw'
    // maxWidth: '65vw',
  },
  title: {
    flexGrow: 1
  },
  grow: {
    flexGrow: 1
  },
  table: {
    '& td:nth-child(4), & th:nth-child(4)': {
      textAlign: 'center !important',
      verticalAlign: 'middle !important'
    }
  },
  selectedRow: {
    outline: '5px solid #FFD700',
    outlineOffset: '-5px'
  },
  anomalyKeepRow: {
    backgroundColor: '#5FB89F'
  },
  anomalyDeleteRow: {
    backgroundColor: '#E06C6C'
  },
  chipOpen: {
    backgroundColor: basicColors.darkRed,
    color: '#fff',
    fontWeight: 600,
    fontSize: '0.75rem',
    minWidth: 70
  },
  chipClosed: {
    backgroundColor: basicColors.darkGreen,
    color: '#fff',
    fontWeight: 600,
    fontSize: '0.75rem',
    minWidth: 70
  }
}))

// componente principale
const AnomalySidenav = ({ className, open, anomaly: propAnomaly, getAnomaliesDevices, onForceClose, onEdit, onClose }) => {
  const anomaly = propAnomaly || {}
  const classes = useStyles()
  // const { enqueueSnackbar } = useSnackbar()
  // console.log('sidenav anomaly =>', anomaly)
  const isExtraSmall = useMediaQuery((theme) => theme.breakpoints.down('xs'))
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down('sm'))
  const isMedium = useMediaQuery((theme) => theme.breakpoints.down('md'))
  // const now = moment()
  const [resources, setResources] = useState({
    hasError: false,
    devices: []
  })
  const [gettingResources, setGettingResources] = useState(true)
  // variabile che gestisce apertura alert conferma chiusura anomalia
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false)
  // variabile che specifica se editare le informazioni
  const [isEditing, setIsEditing] = useState(false)
  // variabile con note e link
  const [notes, setNotes] = useState(anomaly.notes || '')
  const [link, setLink] = useState(anomaly.externalUrl || '')
  const [anomalyPoints, setAnomalyPoints] = useState([])
  const [anomalySerie, setAnomalySerie] = useState([])
  const [anomalyRowSelected, setAnomalyRowSelected] = useState([])
  const anomalyTableRef = useRef(null)
  const anomalyRowRefs = useRef([])

  const { user } = useAuth()

  const permissions = user?.appPermissions || []

  useEffect(() => {
    setNotes(anomaly.notes || '')
    setLink(anomaly.externalUrl || '')

    if (anomaly?.category?.toLowerCase() !== 'misura' || !Array.isArray(anomaly?.metadata?.points)) {
      return
    }

    const anomalyPoints = JSON.parse(JSON.stringify(anomaly.metadata.points))
    for (const p of anomalyPoints) {
      p.selected = false
    }

    setAnomalyPoints(anomalyPoints)

    const rawResource = (Array.isArray(anomaly.relatedResources) && anomaly.relatedResources[0]) || ''
    const deviceId = rawResource.includes('/') ? rawResource.slice(rawResource.lastIndexOf('/') + 1) : rawResource
    if (!deviceId) return

    const firstPointTime = new Date(anomalyPoints[0].time)
    firstPointTime.setMilliseconds(firstPointTime.getMilliseconds() - 1)

    const preQueryTimeFrom = new Date(firstPointTime)
    preQueryTimeFrom.setDate(preQueryTimeFrom.getDate() - 1)
    preQueryTimeFrom.setHours(0, 0, 0, 0)

    const preQuery = {
      deviceId,
      name: anomaly.metadata.property,
      timeFrom: preQueryTimeFrom,
      timeTo: firstPointTime
    }

    const inQuery = {
      deviceId,
      name: anomaly.metadata.property,
      timeFrom: anomalyPoints[0].time,
      timeTo: anomalyPoints[anomalyPoints.length - 1].time
    }

    const lastPointTime = new Date(anomalyPoints[anomalyPoints.length - 1].time)
    lastPointTime.setMilliseconds(lastPointTime.getMilliseconds() + 1)

    const postQueryTimeTo = new Date(lastPointTime)
    postQueryTimeTo.setDate(postQueryTimeTo.getDate() + 1)
    postQueryTimeTo.setHours(23, 59, 59, 999)

    const postQuery = {
      deviceId,
      name: anomaly.metadata.property,
      timeFrom: lastPointTime,
      timeTo: postQueryTimeTo
    }

    Promise.all([
      getDeviceLogs(preQuery),
      getDeviceLogs(inQuery),
      getDeviceLogs(postQuery)
    ])
      .then(responses => {
        const preSerie = []
        const inSerie = []
        const postSerie = []

        const preQueryResponse = responses[0]
        const inQueryResponse = responses[1]
        const postQueryResponse = responses[2]

        const preData = preQueryResponse?.[deviceId]?.[preQuery.name]
        const inData = inQueryResponse?.[deviceId]?.[preQuery.name]
        const postData = postQueryResponse?.[deviceId]?.[preQuery.name]
        if (Array.isArray(preData)) {
          preSerie.push(
            ...preData.slice(0, 20).map(p => ({
              time: p[0],
              value: p[1],
              status: 'check',
              source: 'pre'
            })).toReversed()
          )
        }

        if (Array.isArray(inData)) {
          inSerie.push(
            ...inData.map(p => ({
              time: p[0],
              value: p[1],
              status: 'check',
              source: 'in'
            })).toReversed()
          )
        }

        if (Array.isArray(postData)) {
          postSerie.push(
            ...postData.toReversed().slice(0, 20).map(p => ({
              time: p[0],
              value: p[1],
              status: 'check',
              source: 'post'
            }))
          )
        }

        for (const point of anomalyPoints) {
          const p = inSerie.findIndex(x => x.time === point.time)
          if (p > -1) {
            inSerie[p].source = 'anomaly'
            inSerie[p].value = point.value
          } else {
            inSerie.push({
              time: point.time,
              value: point.value,
              status: 'check',
              source: 'anomaly'
            })
          }
        }

        inSerie.sort((a, b) => new Date(a.time) - new Date(b.time))
        const serie = [
          ...preSerie,
          ...inSerie,
          ...postSerie
        ]

        setAnomalySerie(serie)
        setAnomalyRowSelected(anomalyPoints.map(a => false))
      })

    // return () => {}
  }, [anomaly])

  // funzione che cambia lo stato di modifica
  const toogleEditAnomaly = () => {
    setIsEditing(!isEditing)
  }
  // funzione che conferma / salva le modifiche
  const confirmEdits = (newAnomaly) => {
    alog('dentro confirmEdits', null, 'anomalia')
    try {
      onEdit(newAnomaly)
      setIsEditing(false)
    } catch (err) {
      console.error('confirmEdits err => ', err)
    }
  }

  // funzione che gestisce l'apertura conferma chiusura anomalia
  const handleOpenConfirmDelete = () => {
    setOpenConfirmDelete(true)
  }
  // funzione che gestisce chiusura conferma chiusura anomalia
  const handleCloseConfirmDelete = async (confirmDelete) => {
    // console.log('dentro close confirm delete, confirmDelete => ', confirmDelete)
    if (confirmDelete) {
      // console.log('ho confermato')
      onForceClose(anomaly)
    } else {
      // console.log('non ho confermato')
    }
    setOpenConfirmDelete(false)
  }

  // Effettua il fetch delle risorse associate quando la drawer si apre o cambia anomalia
  useEffect(() => {
    let isCurrent = true
    const directName = anomaly?.resourceName || anomaly?.deviceName || anomaly?.orientationName
    const fetchResources = async () => {
      if (!open || !anomaly?.uuid) {
        setGettingResources(false)
        return
      }
      if (directName && typeof directName === 'string' && !isUUIDOrUri(directName)) {
        setGettingResources(false)
        return
      }
      if (typeof getAnomaliesDevices === 'function') {
        setGettingResources(true)
        try {
          const newResources = await getAnomaliesDevices(anomaly.uuid)
          if (isCurrent) {
            setResources(newResources || { hasError: false, devices: [] })
            setGettingResources(false)
          }
        } catch (err) {
          if (isCurrent) {
            setResources({ hasError: true, devices: [] })
            setGettingResources(false)
          }
        }
      } else {
        if (isCurrent) {
          setGettingResources(false)
        }
      }
    }

    fetchResources()

    return () => {
      isCurrent = false
    }
  }, [open, anomaly?.uuid, anomaly?.resourceName, anomaly?.deviceName, anomaly?.orientationName, getAnomaliesDevices])

  const keepAnomalyPoint = index => {
    const newAnomalyPoints = [...anomalyPoints]
    newAnomalyPoints[index].status = 'keep'
    newAnomalyPoints[index].selected = true
    setAnomalyPoints(newAnomalyPoints)
  }

  const deleteAnomalyPoint = index => {
    const newAnomalyPoints = [...anomalyPoints]
    newAnomalyPoints[index].status = 'delete'
    newAnomalyPoints[index].selected = true
    setAnomalyPoints(newAnomalyPoints)
  }

  const onSelectAnomaly = (event, index) => {
    event.stopPropagation()
    const newAnomalyPoints = [...anomalyPoints]
    newAnomalyPoints[index].selected = !newAnomalyPoints[index].selected
    setAnomalyPoints(newAnomalyPoints)
  }

  const shouldBeIntermediate = rows => rows.some(anomaly => anomaly.selected) && !rows.every(anomaly => anomaly.selected)

  const shouldAllBeChecked = rows => rows.every(anomaly => anomaly.selected)

  const onSelectAllAnomalies = () => {
    const newAnomalyPoints = [...anomalyPoints]
    const listStatus = !newAnomalyPoints.some(anomaly => anomaly.selected)
    for (const p of newAnomalyPoints) {
      p.selected = listStatus
    }

    setAnomalyPoints(newAnomalyPoints)
  }

  const keepSelectedAnomalyPoints = () => {
    const newAnomalyPoints = [...anomalyPoints]
    for (const p of newAnomalyPoints) {
      if (p.selected) {
        p.status = 'keep'
      }
    }

    setAnomalyPoints(newAnomalyPoints)
  }

  const deleteSelectedAnomalyPoints = () => {
    const newAnomalyPoints = [...anomalyPoints]
    for (const p of newAnomalyPoints) {
      if (p.selected) {
        p.status = 'delete'
      }
    }

    setAnomalyPoints(newAnomalyPoints)
  }

  const applyOnSelectedAnomalyPoints = async () => {
    const finalCallback = async () => {
      const newAnomalyPoints = JSON.parse(JSON.stringify(anomalyPoints))
      for (const p of newAnomalyPoints) {
        delete p.source
        delete p.selected
      }

      anomaly.metadata.points = JSON.parse(JSON.stringify(newAnomalyPoints))
      const pointsToDelete = newAnomalyPoints.filter(p => p.status === 'delete')
      if (pointsToDelete.length) {
        await axios.post(`${configuration.apiUrl}/projects/${configuration.projectId}/dashboard/telemetry/deleteAnomalyPoint`,
          anomaly,
          {
            headers: {
              Authorization: `Bearer ${ls.get('accessToken')}`
            }
          }
        )
      }

      await putEditAnomaly(anomaly.uuid, { metadata: anomaly.metadata })
      if (anomaly.metadata.points.length === anomaly.metadata.points.filter(p => p.status !== 'check').length) {
        // Al points are solved, closing the anomaly
        await onForceClose(anomaly)

        // Recalculating metrics
        await axios.post(`${configuration.apiUrl}/projects/${configuration.projectId}/dashboard/recalculations`,
          {
            plantId: anomaly.plantId,
            from: anomaly.metadata.points[0].time,
            to: anomaly.metadata.points.at(-1).time,
            removeExistingMetrics: true,
            deviceId: anomaly.relatedResources[0].slice(anomaly.relatedResources[0].lastIndexOf('/') + 1)
          },
          {
            headers: {
              Authorization: `Bearer ${ls.get('accessToken')}`
            }
          }
        )
      }
    }

    if (anomalyPoints.some(p => p.status === 'delete')) {
      const res = confirm("Hai scelto di eliminare uno o più punti selezionati. L’operazione è irreversibile. Vuole procedere?")
      if (res) {
        await finalCallback()
      }
    } else {
      await finalCallback()
    }
  }

  return (
    anomaly?.category?.toLowerCase() === 'misura'
      ? <Drawer anchor='right' open={open} onClose={onClose} PaperProps={{ style: { width: '30vw' } }}>
        <AppBar position='sticky'>
          <Toolbar
            style={{
              borderLeft: `6px solid ${anomaly.subCategory && anomaly.subCategory.color ? anomaly.subCategory.color : basicColors.primary}`,
              backgroundColor: basicColors.secondary,
              paddingLeft: 16,
              paddingRight: 16
            }}
          >
            <Typography variant='h4' className={classes.title} display='block' noWrap>
              Anomalia: {anomaly.name && anomaly.name !== 'N.F.' ? anomaly.name : (anomaly.metadata?.message || anomaly.configuration?.name || '-')}
            </Typography>
            <div className={classes.grow} />
            <IconButton aria-label='Chiudi anomalia' onClick={onClose} color='inherit'>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <div style={{ padding: 16 }}>
          <Grid container spacing={4} style={{ marginBottom: 16 }}>
            <Grid item xs={12}>
              <Typography variant='h4' display='block' gutterBottom>
                Dettagli
              </Typography>
              <TableContainer component={Paper}>
                <Table aria-labelledby='anomalyStatus' aria-label='Stato generico anomalia'>
                  <TableBody>
                    <TableRow>
                      <TableCell variant='head' style={{ minWidth: 150 }}>
                        Stato
                      </TableCell>
                      <TableCell>
                        {!anomaly.endedAt || anomaly.endedAt === ''
                          ? (
                            <Grid container alignItems='center'>
                              <Grid item xs>
                                <Chip
                                  size='small'
                                  label='Aperta'
                                  className={classes.chipOpen}
                                />
                              </Grid>
                              {permissions.includes('assetsmanager.plants.manage')
                                ? (
                                  <Grid item>
                                    <Tooltip title='Chiudi Anomalia' aria-label='Chiudi Anomalia'>
                                      <Fab size='small' onClick={() => { handleOpenConfirmDelete() }} style={{ backgroundColor: basicColors.darkRed }}>
                                        <SvgIcon fontSize='small'>
                                          <ForceCloseIcon color={basicColors.whiteText} />
                                        </SvgIcon>
                                      </Fab>
                                    </Tooltip>
                                  </Grid>
                                )
                                : null}
                            </Grid>
                          )
                          : (
                            <Chip
                              size='small'
                              label='Chiusa'
                              className={classes.chipClosed}
                            />
                          )}
                      </TableCell>
                    </TableRow>
                    {anomaly.plantName && (
                      <TableRow>
                        <TableCell variant='head'>Impianto</TableCell>
                        <TableCell>{anomaly.plantName}</TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell variant='head'>Risorse coinvolte</TableCell>
                      <TableCell>
                        {anomaly.resourceName || anomaly.deviceName || anomaly.orientationName
                          ? (anomaly.resourceName || anomaly.deviceName || anomaly.orientationName)
                          : gettingResources && !resources.hasError
                            ? (<CircularProgress className={classes.cicle} variant='indeterminate' size={20} thickness={6} />)
                            : resources.hasError
                              ? 'C\'è stato un errore nel reperire le risorse coinvolte.'
                              : resources.devices.length === 0
                                ? 'Non ci sono dispositivi associati a questa anomalia'
                                : resources.devices.map(device => device.name || device.label || device.title || device.uuid).join(', ')}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell variant='head'>Nome</TableCell>
                      <TableCell>{anomaly.name && anomaly.name !== 'N.F.' ? anomaly.name : (anomaly.metadata?.message || anomaly.configuration?.name || '-')}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell variant='head'>Descrizione</TableCell>
                      <TableCell>{anomaly.description && anomaly.description !== 'N.F.' && anomaly.description !== '-' ? anomaly.description : (anomaly.configuration?.description || anomaly.metadata?.message || '-')}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell variant='head'>Categoria</TableCell>
                      <TableCell>
                        {(() => {
                          const catText = typeof anomaly.category === 'object' ? anomaly.category?.label : (anomaly.category || anomaly.configuration?.category)
                          const subText = typeof anomaly.subCategory === 'object' ? anomaly.subCategory?.label : (anomaly.subCategory || anomaly.configuration?.subCategory)
                          if (catText && subText) return `${catText} > ${subText}`
                          if (subText) return subText
                          if (catText) return catText
                          return '-'
                        })()}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Chart
                style={{ height: '30vh' }}
                options={{
                  chart: {
                    id: "anomalies-points-chart",
                    events: {
                      dataPointSelection: (event, chartContext, opts) => {
                        const { dataPointIndex, selectedDataPoints, seriesIndex } = opts
                        if (dataPointIndex === -1) {
                          // Clicked on graph but not on a bar
                          return
                        }

                        if (anomalySerie[dataPointIndex].source !== 'anomaly') {
                          // Clicked on a bar that is not an anomaly
                          return
                        }

                        // Clicked on a bar that is an anomaly, the bar may be selected or unselected
                        const newAnomalyRowSelected = anomalyPoints.map(a => false)
                        if (selectedDataPoints[seriesIndex].length) {
                          // If here, it means that the bar has been selected
                          const nonAnomaliesElements = anomalySerie.slice(0, dataPointIndex).filter(a => a.source !== 'anomaly')
                          const wantedIndex = dataPointIndex - nonAnomaliesElements.length
                          newAnomalyRowSelected[wantedIndex] = true
                          if (anomalyRowRefs.current[wantedIndex]) {
                            anomalyRowRefs.current[wantedIndex].scrollIntoView({
                              behavior: 'smooth',
                              block: 'center'
                            })
                          }
                        }

                        setAnomalyRowSelected(newAnomalyRowSelected)
                      }
                    }
                  },
                  colors: [({ dataPointIndex }) => anomalySerie[dataPointIndex]?.source === 'anomaly' ? '#FF6B6B' : '#48C774'],
                  dataLabels: {
                    enabled: false
                  },
                  tooltip: {
                    theme: 'dark',
                    x: {
                      format: 'yyyy-MM-dd HH:mm:ss'
                    },
                    y: {
                      formatter: (val, opts) => {
                        const originalValue = anomalySerie[opts.dataPointIndex]?.value
                        return originalValue == null ? '' : originalValue
                      }
                    }
                  },
                  xaxis: {
                    type: 'datetime',
                    categories: anomalySerie.map(p => new Date(p.time).getTime()),
                    labels: {
                      datetimeUTC: false,
                      style: {
                        colors: '#ffffff'
                      },
                      datetimeFormatter: {
                        year: 'yyyy',
                        month: "MM yyyy",
                        day: 'yyyy-MM-dd',
                        hour: 'yyyy-MM-dd HH:mm:ss',
                        minute: 'yyyy-MM-dd HH:mm:ss'
                      }
                    }
                  },
                  yaxis: {
                    labels: {
                      style: {
                        colors: '#ffffff'
                      }
                    },
                    decimalsInFloat: 3
                  }
                }}
                series={[
                  {
                    name: "Valore",
                    data: anomalySerie.map(p => ({
                      x: new Date(p.time).getTime(),
                      y: p.value == null ? null : String(Number(p.value) + 0.5)
                    }))
                  }
                ]}
                type="bar"
              />
              <Grid container alignItems='flex-start' justifyContent='flex-start' spacing={1} style={{ marginBottom: 10 }}>
                <Grid item xs={4}>
                  <Tooltip title='Valida punti selezionati' aria-label='Valida punti selezionati' placement='top'>
                    <Button onClick={() => { keepSelectedAnomalyPoints() }} style={{ backgroundColor: basicColors.darkGreen, width: '100%' }}>
                      <OkIconSquare color={basicColors.whiteText} />
                    </Button>
                  </Tooltip>
                </Grid>
                <Grid item xs={4}>
                  <Tooltip title='Elimina punti selezionati' aria-label='Elimina punti selezionati' placement='top'>
                    <Button onClick={() => { deleteSelectedAnomalyPoints() }} style={{ backgroundColor: basicColors.darkRed, width: '100%' }}>
                      <CloseIconSquare color={basicColors.whiteText} />
                    </Button>
                  </Tooltip>
                </Grid>
                <Grid item xs={4}>
                  <Tooltip title='Applica azioni su punti selezionati' aria-label='Applica azioni su punti selezionati' placement='top'>
                    <Button onClick={() => { applyOnSelectedAnomalyPoints() }} style={{ backgroundColor: basicColors.blueGrayMedium, width: '100%' }}>
                      <LogOutIcon color={basicColors.whiteText} />
                    </Button>
                  </Tooltip>
                </Grid>
              </Grid>
              <TableContainer component={Paper} style={{ height: '26vh', overflow: 'auto' }}>
                <Table ref={anomalyTableRef} className={classes.table} stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding='checkbox'>
                        <Tooltip title='Seleziona tutti' aria-label='Seleziona tutti' placement='top'>
                          <Checkbox
                            indeterminate={shouldBeIntermediate(anomalyPoints)}
                            checked={shouldAllBeChecked(anomalyPoints)}
                            onChange={onSelectAllAnomalies}
                            inputProps={{ 'aria-label': 'Select all anomalies' }}
                            color='primary'
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>Istante</TableCell>
                      <TableCell>Valore</TableCell>
                      <TableCell>Azioni</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {anomalyPoints.map((p, index) => (
                      <TableRow
                        ref={el => anomalyRowRefs.current[index] = el}
                        key={index}
                        className={clsx([
                          anomalyRowSelected[index]
                            ? classes.selectedRow
                            : null,
                          p.status === 'keep'
                            ? classes.anomalyKeepRow
                            : p.status === 'delete'
                              ? classes.anomalyDeleteRow
                              : null
                        ])}
                      >
                        <TableCell padding='checkbox'>
                          <Checkbox
                            checked={p.selected}
                            onClick={(event) => onSelectAnomaly(event, index)}
                            inputProps={{
                              'aria-labelledby': `anomalies-checkbox-${index}`
                            }}
                            color='primary'
                          />
                        </TableCell>
                        <TableCell>
                          {moment(p.time).format('yyyy-MM-DD HH:mm:ss')}
                        </TableCell>
                        <TableCell>
                          {p.value}
                        </TableCell>
                        <TableCell>
                          <Grid container alignItems='center' justifyContent='center' spacing={1}>
                            <Grid item>
                              <Tooltip title='Valida punto' aria-label='Valida punto' placement='top'>
                                <Fab size='small' onClick={() => { keepAnomalyPoint(index) }} style={{ backgroundColor: basicColors.darkGreen }}>
                                  <SvgIcon fontSize='small'>
                                    <OkIcon color={basicColors.whiteText} />
                                  </SvgIcon>
                                </Fab>
                              </Tooltip>
                            </Grid>
                            <Grid item>
                              <Tooltip title='Elimina punto' aria-label='Elimina punto' placement='top'>
                                <Fab size='small' onClick={() => { deleteAnomalyPoint(index) }} style={{ backgroundColor: basicColors.darkRed }}>
                                  <SvgIcon fontSize='small'>
                                    <CloseIcon color={basicColors.whiteText} />
                                  </SvgIcon>
                                </Fab>
                              </Tooltip>
                            </Grid>
                          </Grid>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
          <ConfirmationDialogRaw
            open={openConfirmDelete}
            onClose={handleCloseConfirmDelete}
            title='Conferma eliminazione Datalogger'
            description={'Sei sicuro di voler chiudere l\'anomalia?'}
          />
        </div>
      </Drawer>
      : <Drawer anchor='right' open={open} onClose={onClose}>
        <Box
          display='block'
          className={classes.sidenavContainer}
          style={{ width: isExtraSmall ? '100vw' : isSmall ? '78vw' : isMedium ? '55vw' : '45vw' }}
        >
          <AppBar position='sticky'>
            <Toolbar
              style={{
                borderLeft: `6px solid ${anomaly.subCategory && anomaly.subCategory.color ? anomaly.subCategory.color : basicColors.primary}`,
                backgroundColor: basicColors.secondary,
                paddingLeft: 16,
                paddingRight: 16
              }}
            >
              <Typography variant='h4' className={classes.title} display='block' noWrap>
                Anomalia: {anomaly.name && anomaly.name !== 'N.F.' ? anomaly.name : (anomaly.metadata?.message || anomaly.configuration?.name || '-')}
              </Typography>
              <div className={classes.grow} />
              <IconButton aria-label='Chiudi anomalia' onClick={onClose} color='inherit'>
                <CloseIcon />
              </IconButton>
            </Toolbar>
          </AppBar>
          <div style={{ padding: 16 }}>
            <Grid container spacing={4} style={{ marginBottom: 16 }}>
              <Grid item xs={12}>
                <Typography variant='h4' display='block' gutterBottom>
                  Dettagli
                </Typography>
                <TableContainer component={Paper}>
                  <Table aria-labelledby='anomalyStatus' aria-label='Stato generico anomalia'>
                    <TableBody>
                      <TableRow>
                        <TableCell variant='head' style={{ minWidth: 150 }}>
                          Stato
                        </TableCell>
                        <TableCell>
                          {!anomaly.endedAt || anomaly.endedAt === ''
                            ? (
                              <Grid container alignItems='center'>
                                <Grid item xs>
                                  <Chip
                                    size='small'
                                    label='Aperta'
                                    className={classes.chipOpen}
                                  />
                                </Grid>
                                {permissions.includes('assetsmanager.plants.manage')
                                  ? (
                                    <Grid item>
                                      <Tooltip title='Chiudi Anomalia' aria-label='Chiudi Anomalia'>
                                        <Fab size='small' onClick={() => { handleOpenConfirmDelete() }} style={{ backgroundColor: basicColors.darkRed }}>
                                          <SvgIcon fontSize='small'>
                                            <ForceCloseIcon color={basicColors.whiteText} />
                                          </SvgIcon>
                                        </Fab>
                                      </Tooltip>
                                    </Grid>
                                  )
                                  : null}
                              </Grid>
                            )
                            : (
                              <Chip
                                size='small'
                                label='Chiusa'
                                className={classes.chipClosed}
                              />
                            )}
                        </TableCell>
                      </TableRow>
                      {anomaly.plantName && (
                        <TableRow>
                          <TableCell variant='head'>Impianto</TableCell>
                          <TableCell>{anomaly.plantName}</TableCell>
                        </TableRow>
                      )}
                      <TableRow>
                        <TableCell variant='head'>Risorse coinvolte</TableCell>
                        <TableCell>
                          {anomaly.resourceName || anomaly.deviceName || anomaly.orientationName
                            ? (anomaly.resourceName || anomaly.deviceName || anomaly.orientationName)
                            : gettingResources && !resources.hasError
                              ? (<CircularProgress className={classes.cicle} variant='indeterminate' size={20} thickness={6} />)
                              : resources.hasError
                                ? 'C\'è stato un errore nel reperire le risorse coinvolte.'
                                : resources.devices.length === 0
                                  ? 'Non ci sono dispositivi associati a questa anomalia'
                                  : resources.devices.map(device => device.name || device.label || device.title || device.uuid).join(', ')}
                        </TableCell>
                      </TableRow>
                      {anomaly.severity && (!anomaly.endedAt || anomaly.endedAt === '')
                        ? (
                          <TableRow>
                            <TableCell variant='head'>Severity attuale</TableCell>
                            <TableCell>{anomaly.severity[anomaly.severity.length - 1].level}</TableCell>
                          </TableRow>
                        )
                        : null}
                      <TableRow>
                        <TableCell variant='head'>Inizio</TableCell>
                        <TableCell>{anomaly.startedAt ? moment(anomaly.startedAt).format('DD/MM/yyyy HH:mm') : '-'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell variant='head'>Fine</TableCell>
                        <TableCell>
                          {anomaly.endedAt && anomaly.endedAt !== ''
                            ? moment(anomaly.endedAt).format('DD/MM/yyyy HH:mm')
                            : '-'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell variant='head'>Durata</TableCell>
                        <TableCell>{anomaly.duration != null ? returnDuration(anomaly.duration) : '-'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell variant='head'>Nome</TableCell>
                        <TableCell>{anomaly.name && anomaly.name !== 'N.F.' ? anomaly.name : (anomaly.metadata?.message || anomaly.configuration?.name || '-')}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell variant='head'>Descrizione</TableCell>
                        <TableCell>{anomaly.description && anomaly.description !== 'N.F.' && anomaly.description !== '-' ? anomaly.description : (anomaly.configuration?.description || anomaly.metadata?.message || '-')}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell variant='head'>Categoria</TableCell>
                        <TableCell>
                          {(() => {
                            const catText = typeof anomaly.category === 'object' ? anomaly.category?.label : (anomaly.category || anomaly.configuration?.category)
                            const subText = typeof anomaly.subCategory === 'object' ? anomaly.subCategory?.label : (anomaly.subCategory || anomaly.configuration?.subCategory)
                            if (catText && subText) return `${catText} > ${subText}`
                            if (subText) return subText
                            if (catText) return catText
                            return '-'
                          })()}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid item xs={12}>
                <Grid container alignItems='center' justifyContent='center'>
                  <Grid item xs>
                    <Typography variant='h5' display='block' gutterBottom>
                      Informazioni aggiuntive
                    </Typography>
                  </Grid>
                  {permissions.includes('assetsmanager.plants.manage')
                    ? (
                      <Grid item>
                        {!isEditing && (
                          <Tooltip title='Modifica Anomalia' aria-label='Modifica informazioni'>
                            <IconButton onClick={() => { toogleEditAnomaly() }}>
                              <SvgIcon size='small'>
                                <EditIcon color={basicColors.darkYellow} />
                              </SvgIcon>
                            </IconButton>
                          </Tooltip>
                        )}
                        {isEditing && (
                          <Tooltip title='Chiudi Modifica' aria-label='Chiudi Modifica'>
                            <IconButton onClick={() => { toogleEditAnomaly() }}>
                              <SvgIcon size='small'>
                                <CloseIcon color={basicColors.darkYellow} />
                              </SvgIcon>
                            </IconButton>
                          </Tooltip>
                        )}
                        {isEditing && (
                          <Tooltip title='Conferma modifiche' aria-label='Conferma modifiche'>
                            <IconButton
                              onClick={() => {
                                const newAnomaly = JSON.parse(JSON.stringify(anomaly))
                                newAnomaly.notes = notes
                                newAnomaly.externalUrl = link
                                if (!newAnomaly.externalUrl.startsWith('http')) {
                                  newAnomaly.externalUrl = 'https://' + newAnomaly.externalUrl
                                }
                                confirmEdits(newAnomaly)
                              }}
                            >
                              <SvgIcon size='small'>
                                <OkIcon color={basicColors.darkGreen} />
                              </SvgIcon>
                            </IconButton>
                          </Tooltip>
                        )}
                      </Grid>
                    )
                    : null}
                </Grid>
                <TableContainer component={Paper}>
                  <Table aria-labelledby='anomalyStatus' aria-label='Stato generico anomalia'>
                    <TableBody>
                      <TableRow>
                        <TableCell variant='head'>Link esterno</TableCell>
                        <TableCell>
                          {!isEditing && (
                            <Grid container alignItems='center' justifyContent='space-between'>
                              <Grid item style={{ maxWidth: '280px' }}>
                                {anomaly.externalUrl
                                  ? (
                                    <Typography component='div' noWrap>
                                      <Link href={anomaly.externalUrl} noWrap target='_blank' rel='noopener noreferrer' variant='h5'>
                                        {anomaly.externalUrl}
                                      </Link>
                                    </Typography>
                                  )
                                  : <Typography variant='body2'>Nessun link inserito...</Typography>}
                              </Grid>
                              <Grid item>
                                <Tooltip title='Copia Link' aria-label='Copia Link'>
                                  <IconButton onClick={() => { copyTextToClipboard(anomaly.externalUrl) }}>
                                    <SvgIcon>
                                      <CopyIcon color={basicColors.whiteText} />
                                    </SvgIcon>
                                  </IconButton>
                                </Tooltip>
                              </Grid>
                              {/* <Grid item>
                              <Tooltip title='Apri Link' aria-label='Apri Link'>
                                <IconButton onClick={() => { openInNewTab(anomaly.externalUrl) }}>
                                  <SvgIcon>
                                    <OpenLinkIcon color={basicColors.primary} />
                                  </SvgIcon>
                                </IconButton>
                              </Tooltip>
                            </Grid> */}
                            </Grid>
                          )}
                          {isEditing && (
                            <TextField
                              id='link'
                              fullWidth
                              name='link'
                              value={link || ''}
                              placeholder='Link esterno'
                              variant='outlined'
                              // style={{ marginTop: 20 }}
                              onChange={(event) => {
                                // alog('new plantStatusNotes =>', event.target.value, 'report')
                                const newLink = event.target.value
                                setLink(newLink)
                              }}
                            />
                          )}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell variant='head'>Note</TableCell>
                        <TableCell>
                          <TextField
                            id='notes'
                            fullWidth
                            // label='Note'
                            disabled={!isEditing}
                            name='notes'
                            multiline
                            value={notes || ''}
                            placeholder='Non ci sono note'
                            minRows={4}
                            maxRows={6}
                            variant='outlined'
                            // style={{ marginTop: 20 }}
                            onChange={(event) => {
                              // alog('new plantStatusNotes =>', event.target.value, 'report')
                              const newNote = event.target.value
                              setNotes(newNote)
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid item xs={12}>
                <Typography variant='h4' display='block' gutterBottom>
                  Severity
                </Typography>
                <TableContainer component={Paper}>
                  <Table aria-labelledby='anomalyStatus' aria-label='Stato generico anomalia'>
                    <TableHead>
                      <TableRow>
                        <TableCell>Livello</TableCell>
                        <TableCell>Inizio</TableCell>
                        <TableCell>Fine</TableCell>
                        <TableCell>Durata</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {anomaly.severity && anomaly.severity.length > 0
                        ? (anomaly.severity.map((sev, index) => {
                          return (
                            <TableRow key={index}>
                              <TableCell>{sev.level || '-'}</TableCell>
                              <TableCell>
                                {sev.startedAt && sev.startedAt !== ''
                                  ? moment(sev.startedAt).format('DD/MM/yyyy HH:mm')
                                  : '-'}
                              </TableCell>
                              <TableCell>
                                {sev.endedAt && sev.endedAt !== '' ? moment(sev.endedAt).format('DD/MM/yyyy HH:mm') : '-'}
                              </TableCell>
                              <TableCell>
                                {sev.duration
                                  ? returnDuration(sev.duration)
                                  : '-'}
                              </TableCell>
                            </TableRow>
                          )
                        }))
                        : (
                          <TableRow>
                            <TableCell colSpan={4}>Non ci sono severity.</TableCell>
                          </TableRow>
                        )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
            <ConfirmationDialogRaw
              open={openConfirmDelete}
              onClose={handleCloseConfirmDelete}
              title='Conferma eliminazione Datalogger'
              description={'Sei sicuro di voler chiudere l\'anomalia?'}
            />
          </div>
        </Box>
      </Drawer>
  )
}

AnomalySidenav.propTypes = {
  className: PropTypes.string,
  open: PropTypes.bool.isRequired,
  anomaly: PropTypes.object,
  getAnomaliesDevices: PropTypes.func,
  onClose: PropTypes.func,
  onForceClose: PropTypes.func,
  onEdit: PropTypes.func
}

AnomalySidenav.defaultProps = {
  open: false,
  anomaly: {}
}

export default AnomalySidenav
